import React, { useState } from 'react';
import {
  Box, Stack, Typography, TextField, FormControl, InputLabel,
  Select, MenuItem, Button, Chip, RadioGroup, Radio, FormControlLabel,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { fetchWithAuth } from '../../utils/api';
import BulletList from '../../components/layout/BulletList';

const AdminAddItem = () => {
  const theme = useTheme();

  const [type, setType] = useState('');
  const [isStable, setIsStable] = useState('');
  const [isPrismatic, setIsPrismatic] = useState('');
  const [notes, setNotes] = useState('');
  const [rawItemInput, setRawItemInput] = useState('');
  const [items, setItems] = useState([]);
  const [submitSummary, setSubmitSummary] = useState(null);

  const guidelines = [
    "Grams themselves and their bundles should not be added because they are not tradeable. Items in the grams should be added.",
    "Do not add deluxe item bundle names because they are not tradeable. Instead, add the individual items that are tradeable.",
    "Caps are added as nonwearables, but cap bundles should be added as type special.",
    "Superpacks are added under type special since people trade it for custom like cap bundles.",
    "Elite boutique items should be added so it will show notes in DTI and userscript. I think we have some as type wearable, but moving forward let's add as type special.",
    "If you add an elite boutique item, but sure to change the note after to say Permanent Buyable - Elite Boutique (NO TRADE)",
    "LE boxes (from gbcs) and other nontradeable items should not be added.",
  ];

  const handleAddItems = () => {
    const parsed = rawItemInput
      .split(',')
      .map(i => i.trim())
      .filter(i => i.length > 0);

    if (parsed.length === 0) return;

    const combined = [...items, ...parsed];

    // Remove duplicates (case-insensitive)
    const uniqueMap = new Map();
    for (const item of combined) {
      const lower = item.toLowerCase();
      if (!uniqueMap.has(lower)) {
        uniqueMap.set(lower, item); // Preserve original casing
      }
    }

    const uniqueItems = Array.from(uniqueMap.values());

    if (uniqueItems.length === items.length) {
      return alert('All items are duplicates.');
    }

    setItems(uniqueItems);
    setRawItemInput('');
  };

  const handleRemoveItem = (item) => {
    setItems(prev => prev.filter(i => i !== item));
  };

  const handleSubmit = async () => {
    const itemPayload = {
      type,
      is_stable: isStable === 'yes',
      is_prismatic: isPrismatic === 'yes',
      notes,
      items,
    };

    try {
      const res = await fetchWithAuth("/items/bulk-add", {
        method: "POST",
        body: itemPayload,
      });

      setSubmitSummary(res);
    } catch (err) {
      console.error(err);
      alert("Error adding items");
    }
  };

  const isFormValid = type && isStable && isPrismatic && items.length > 0 && notes.length <= 50;

  const handleCloseSummary = () => {
    setSubmitSummary(null);
    setType('');
    setIsStable('');
    setIsPrismatic('');
    setNotes('');
    setItems([]);
    setRawItemInput('');
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', mt: 6, px: 3 }}>
      <Box
        sx={{
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
          borderRadius: 2,
          p: 4,
          boxShadow: 3,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 3, color: theme.palette.accent.main }}>
          admin: add Items
        </Typography>

        <Stack spacing={3}>
          <FormControl fullWidth>
            <InputLabel id="item-type-label">Type of Item</InputLabel>
            <Select labelId="item-type-label" label="Type of Item" value={type} onChange={(e) => setType(e.target.value)}>
              <MenuItem value="wearable">wearable</MenuItem>
              <MenuItem value="nonwearable">nonwearable</MenuItem>
              <MenuItem value="token">token</MenuItem>
              <MenuItem value="essence">essence</MenuItem>
              <MenuItem value="special">special</MenuItem>
            </Select>
          </FormControl>

          <FormControl>
            <Typography variant="subtitle1">Is it stable?</Typography>
            <RadioGroup
              row
              value={isStable}
              onChange={(e) => setIsStable(e.target.value)}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>

          <FormControl>
            <Typography variant="subtitle1">Is this a prismatic token?</Typography>
            <RadioGroup
              row
              value={isPrismatic}
              onChange={(e) => setIsPrismatic(e.target.value)}
            >
              <FormControlLabel value="yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="no" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>

          <TextField
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            inputProps={{ maxLength: 50 }}
            fullWidth
          />

          <TextField
            label="Items (comma separated)"
            value={rawItemInput}
            onChange={(e) => setRawItemInput(e.target.value)}
            multiline
            minRows={4}
            maxRows={10}
            fullWidth
            inputProps={{
              style: {
                whiteSpace: "pre-wrap",
                overflowWrap: "break-word",
                scrollbarWidth: "thin",              // Firefox width
                scrollbarColor: "#999 transparent",  // Firefox color
              }
            }}
            sx={{
              '& .MuiInputBase-inputMultiline': {
                overflowY: 'auto',
                maxHeight: '200px',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: 'rgba(0,0,0,0.2)',
                  borderRadius: '4px',
                },
                '&::-webkit-scrollbar-track': {
                  backgroundColor: 'transparent',
                },
              }
            }}
          />

          <Button
            variant="outlined"
            color="secondary"
            onClick={handleAddItems}
            disabled={!rawItemInput.trim()}
          >
            Add Item(s)
          </Button>

          {items.length > 0 && (
            <>
              <Typography variant="subtitle1">Items to be added:</Typography>
              <Stack
                direction="row"
                flexWrap="wrap"
                sx={{ gap: 1 }}
              >
                {items.map((item, idx) => (
                  <Chip
                    key={idx}
                    label={item}
                    onDelete={() => handleRemoveItem(item)}
                    onClick={() => { }}
                    clickable={false}
                  />
                ))}
              </Stack>
            </>
          )}

          <Button
            variant="contained"
            fullWidth
            disabled={!isFormValid}
            onClick={handleSubmit}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.background.paper,
              fontWeight: 'bold',
              '&:hover': {
                opacity: 0.9,
              },
            }}
          >
            Submit
          </Button>
        </Stack>
      </Box>

      {/* Summary Modal */}
      <Dialog open={!!submitSummary} onClose={handleCloseSummary}>
        <DialogTitle>Bulk Add Summary</DialogTitle>
        <DialogContent
          dividers
          sx={{
            maxHeight: '60vh',
            overflowY: 'auto',
          }}
        >
          {submitSummary?.inserted?.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ mt: 1 }}>Inserted Items:</Typography>
              <List dense>
                {submitSummary.inserted.map((item, idx) => (
                  <ListItem key={`inserted-${idx}`}>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </>
          )}
          {submitSummary?.skipped?.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ mt: 2 }}>Skipped (already exist but double check these):</Typography>
              <List dense>
                {submitSummary.skipped.map((item, idx) => (
                  <ListItem key={`skipped-${idx}`}>
                    <ListItemText primary={item} />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseSummary} color="primary" variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>


      <Box sx={{ mt: 2 }}>

        <Typography variant="h5" sx={{ fontWeight: 'bold', color: theme.palette.accent.main }}>
          guidelines
        </Typography>

        <BulletList items={guidelines} />

      </Box>
    </Box>
  );
};

export default AdminAddItem;
